import {useEagGridStore} from '../store/useEagGridStore';
import type {ActionCallback} from '../types/ActionCallback';
import type {RowData} from '../types/RowData';

interface UseBulkActionExecutionOptions {
  actionCallback: ActionCallback | undefined;
  rows: RowData[];
  selectedRowIds: Set<string>;
  gridCode: string;
  /** Stable refresh callback — reads from refs at call time, does not change identity. */
  refreshData: () => void;
}

/**
 * Returns a callback that executes a bulk action by calling `actionCallback`
 * with an array of selected row IDs and their data.
 *
 * If the callback returns a Promise, the hook sets `isBulkActionLoading` in the
 * store to `true` until the Promise settles. While loading, subsequent calls are
 * silently ignored (double-click protection).
 *
 * No manual useCallback — React Compiler memoizes this hook's return value automatically.
 */
export function useBulkActionExecution(options: UseBulkActionExecutionOptions) {
  const store = useEagGridStore();

  return (actionKey: string) => {
    if (!options.actionCallback) {
      return;
    }

    // Double-click protection: ignore if a bulk action is already in flight
    if (store.getState().isBulkActionLoading) {
      return;
    }

    const selectedRows = options.rows.filter((row) => options.selectedRowIds.has(row.id));
    const selectedIds = selectedRows.map((row) => row.id);

    const result = options.actionCallback({
      actionKey,
      rowId: selectedIds,
      rowData: selectedRows,
      refreshData: options.refreshData,
      refreshCells: () => undefined,
      redrawRows: () => undefined,
      deselectAll: () => store.deselectAll(),
      getColumnData: () => null,
      sourceSearchParams: '',
      queryId: store.getState().queryId,
      actionLock: {
        lock: () => undefined,
        unlock: () => undefined,
      },
    });

    // Handle Promise return — mirrors useRowActionClick pattern
    if (
      result !== null &&
      result !== undefined &&
      typeof (result as Promise<unknown>).then === 'function'
    ) {
      store.setIsBulkActionLoading(true);

      (result as Promise<string | void>)
        .catch(() => {
          // Consumer handles error display (toast, etc.)
        })
        .finally(() => {
          store.setIsBulkActionLoading(false);
        });
    }
  };
}
